import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AuthError, signup } from "@/services/auth.service";
import { loadLegalDocBody } from "@/services/legalDoc.service";
import { useAuthScreenChrome } from "@/hooks/useAuthScreenChrome";
import { LegalLinks } from "@/components/ui/LegalLinks";
import { DatePicker } from "@/components/ui/DatePicker";
import { Select } from "@/components/ui/Select";
import { Icon } from "@/components/ui/Icon";
import { ACCOUNT_PRIVACY_NOTICE, JOINT_USE_CONSENT_NOTICE } from "@/domain/consent";
import { formatPhoneKR } from "@/domain/employee";
import { PASSWORD_MIN_LENGTH, passwordStrength } from "@/domain/passwordStrength";
import { hangulToQwerty } from "@/utils/hangulToQwerty";
import { SPACE_META, TENANT_KEYS, isTenantKey, type TenantKey } from "@/domain/space";

const ERROR_MESSAGE: Record<string, string> = {
  ID_TAKEN: "이미 사용 중인 아이디입니다. 다른 아이디를 입력하세요.",
  WEAK_PASSWORD: "비밀번호가 너무 약합니다. 6자 이상으로 입력하세요.",
};

// 로그인 화면과 동일한 아이디 입력 허용 문자 집합
const ID_ALLOW = /[^a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/g;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 이메일 도메인 선택 — 국내에서 많이 쓰는 순. "직접 입력"이 기본 */
const EMAIL_DOMAIN_DIRECT = "direct";
const EMAIL_DOMAINS = ["naver.com", "gmail.com", "daum.net", "hanmail.net", "kakao.com", "nate.com", "hotmail.com", "outlook.com"];
/** 근무 법인(실제로 일하는 곳. 급여 법인은 관리자가 급여 명부 연결로 따로 정한다) — 고른 법인의 법인 관리자(또는 전체 관리자)가 승인한다. 법인 목록·이름은 domain/space 한 곳 */
const TENANT_OPTIONS = TENANT_KEYS.map((t) => ({
  value: t,
  label: SPACE_META[t].sub ? `${SPACE_META[t].label} (${SPACE_META[t].sub})` : SPACE_META[t].label,
}));

const EMAIL_DOMAIN_OPTIONS = [
  { value: EMAIL_DOMAIN_DIRECT, label: "직접 입력" },
  ...EMAIL_DOMAINS.map((d) => ({ value: d })),
];

/** 고지 항목 표 — 동의 항목 스크롤 박스 안에 쓴다. */
function NoticeList({ rows }: { rows: ReadonlyArray<readonly [string, string]> }) {
  return (
    <dl>
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const SCROLL_TIP = "내용을 끝까지 내리면 체크할 수 있습니다";

/**
 * 잠긴 체크를 눌렀을 때만 잠깐 뜨는 말풍선. 부모가 누를 때마다 key(횟수)를 바꿔 새로 붙이고,
 * 나타남·사라짐은 CSS 애니메이션이 맡는다(타이머·상태 없음).
 */
function ClickTip({ text }: { text: string }) {
  return (
    <span className="signup-click-tip" role="status">
      {text}
    </span>
  );
}

/** 박스 좌우 떨림 — 박스를 다시 그리지 않고(스크롤 위치 유지) 애니메이션만 재생. 움직임 줄이기 설정이면 생략 */
function shake(el: HTMLElement | null) {
  if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  el.animate(
    [
      { transform: "translateX(0)" },
      { transform: "translateX(-6px)" },
      { transform: "translateX(5px)" },
      { transform: "translateX(-4px)" },
      { transform: "translateX(3px)" },
      { transform: "translateX(0)" },
    ],
    { duration: 360, easing: "ease-out" },
  );
}

type ConsentItemProps = {
  title: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  read: boolean;
  onRead: () => void;
  /** false 면 아직 내용을 불러오는 중 — 짧은 안내 문구만으로 "다 읽음" 처리되지 않게 판정을 미룬다. */
  ready?: boolean;
  /** 원문 새 탭 링크(공개 문서가 따로 있을 때) */
  fullHref?: string;
  /** 안 읽고 체크를 시도한 횟수 — 0 보다 크면 박스 빨간 테두리, 늘 때마다 떨림 */
  nudgeCount: number;
  /** 이 항목 체크 줄에 말풍선을 띄울 순번(0 = 없음). 바뀔 때마다 새로 띄움 */
  tipSeq: number;
  onNudge: () => void;
  children: ReactNode;
};

/**
 * 동의 항목 한 줄 — 고정 높이 스크롤 박스를 끝까지 내려야 체크박스가 풀리고, 그때 자동 체크된다(2026-10-26 사용자 요청).
 * 잠긴 체크를 누르면 말풍선으로 안내하고 박스를 빨간 테두리 + 떨림으로 표시한다(안내 문구 줄은 두지 않음).
 * 내용이 박스보다 짧으면 처음부터 다 읽은 것으로 본다. 다 읽은 상태는 위(Signup)가 들고 있어
 * 2단계에서 [이전]으로 돌아와도 다시 내릴 필요가 없다.
 */
function ConsentItem({
  title,
  checked,
  onChange,
  read,
  onRead,
  ready = true,
  fullHref,
  nudgeCount,
  tipSeq,
  onNudge,
  children,
}: ConsentItemProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (nudgeCount > 0) shake(boxRef.current);
  }, [nudgeCount]);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkEnd = useCallback(() => {
    const el = boxRef.current;
    if (!el || read || !ready) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) onRead();
  }, [read, ready, onRead]);

  useEffect(() => {
    checkEnd();
    const ro = new ResizeObserver(checkEnd);
    if (boxRef.current) ro.observe(boxRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [checkEnd]);

  return (
    <div className="signup-consent-item">
      <div className="signup-consent-head">
        <label className={`signup-consent-check${read ? "" : " is-locked"}`}>
          <input
            type="checkbox"
            checked={checked}
            aria-disabled={!read || undefined}
            // 잠긴 동안은 체크 대신 말풍선·빨간 테두리·떨림으로 안내(disabled 면 누름 자체를 못 받음)
            onChange={(e) => (read ? onChange(e.target.checked) : onNudge())}
          />
          <span>
            <strong>[필수]</strong> {title}
          </span>
        </label>
        {tipSeq > 0 && !read && <ClickTip key={tipSeq} text={SCROLL_TIP} />}
        {fullHref && (
          <a className="signup-consent-link" href={fullHref} target="_blank" rel="noopener noreferrer">
            새 창으로 보기
          </a>
        )}
      </div>
      <div
        ref={boxRef}
        className={`signup-consent-box${nudgeCount > 0 && !read ? " is-nudge" : ""}`}
        tabIndex={0}
        role="region"
        aria-label={`${title} 내용`}
        onScroll={checkEnd}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
}

type PasswordInputProps = {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  invalid?: boolean;
  describedBy?: string;
};

/** 비밀번호 입력 + 오른쪽 보기/숨기기 버튼 */
function PasswordInput({ id, value, onChange, placeholder, invalid, describedBy }: PasswordInputProps) {
  const [shown, setShown] = useState(false);
  return (
    <div className="signup-input-wrap">
      <input
        id={id}
        type={shown ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="new-password"
        required
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
      <button
        type="button"
        className="signup-input-action"
        aria-label={shown ? "비밀번호 숨기기" : "비밀번호 보기"}
        aria-pressed={shown}
        onClick={() => setShown((v) => !v)}
      >
        <Icon name={shown ? "eyeOff" : "eye"} size={20} />
      </button>
    </div>
  );
}

type FieldKey = "tenant" | "name" | "id" | "password" | "passwordConfirm" | "email" | "phone" | "birth";

type FieldBoxProps = {
  field: FieldKey;
  /** 이 칸의 오류 문구(없으면 null) — 빨간 테두리 + 말풍선 */
  error: string | null;
  /** 말풍선 순번 — 가입 신청을 누를 때마다 바뀌어 말풍선을 처음부터 다시 띄움 */
  tipSeq: number;
  children: ReactNode;
};

/** 입력칸 테두리 상자 — 가입 신청 검사에서 걸린 칸을 빨간 테두리·말풍선으로 표시(떨림은 onSubmit 에서 shake) */
function FieldBox({ field, error, tipSeq, children }: FieldBoxProps) {
  return (
    <div data-field={field} className={`signup-field-box${error ? " is-error" : ""}`}>
      {children}
      {error && <ClickTip key={tipSeq} text={error} />}
    </div>
  );
}

/**
 * 회원가입 — 1단계 약관·동의 → 2단계 계정 정보 (2026-10-26 순서 변경).
 * 필수 동의를 마쳐야 2단계로 넘어가고, 2단계에서 [이전]으로 돌아가도 입력값은 유지된다.
 */
export function Signup() {
  useAuthScreenChrome();
  const [step, setStep] = useState<1 | 2>(1);
  // 근무 법인 — 빈 값 = 아직 안 고름(placeholder)
  const [tenant, setTenant] = useState<TenantKey | "">("");
  const [loginId, setLoginId] = useState("");
  // 가입 신청 때 이미 있다고 확인된 아이디 — 입력이 이 값과 같은 동안 아이디 칸에 오류 표시
  const [takenId, setTakenId] = useState("");
  const idTaken = takenId !== "" && loginId.trim() === takenId;
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  // 이메일 = 아이디 @ 도메인. 도메인은 직접 입력(기본) 또는 목록 선택
  const [emailLocal, setEmailLocal] = useState("");
  const [emailDomain, setEmailDomain] = useState("");
  const [emailDomainChoice, setEmailDomainChoice] = useState(EMAIL_DOMAIN_DIRECT);
  const emailDomainRef = useRef<HTMLInputElement>(null);
  const personalEmail = emailLocal || emailDomain ? `${emailLocal.trim()}@${emailDomain.trim()}` : "";
  const [birthDate, setBirthDate] = useState("");
  const [termsConsent, setTermsConsent] = useState(false);
  const [jointUseConsent, setJointUseConsent] = useState(false);
  const [privacyNoticeConsent, setPrivacyNoticeConsent] = useState(false);
  const allAgreed = termsConsent && jointUseConsent && privacyNoticeConsent;
  // 항목별 "끝까지 읽음" — 체크박스 잠금 해제 조건
  const [termsRead, setTermsRead] = useState(false);
  const [jointUseRead, setJointUseRead] = useState(false);
  const [privacyNoticeRead, setPrivacyNoticeRead] = useState(false);
  const allRead = termsRead && jointUseRead && privacyNoticeRead;
  // 안 읽고 체크를 시도한 항목 — 박스 빨간 테두리(읽으면 자동으로 사라짐)
  const [nudged, setNudged] = useState({ terms: 0, joint: 0, privacy: 0 });
  // 말풍선은 누른 자리 한 곳에만 — 전체 동의를 누르면 전체 동의 줄에만 뜨고 박스들은 테두리·떨림만
  const [tip, setTip] = useState<{ at: "terms" | "joint" | "privacy" | "all"; seq: number }>({ at: "all", seq: 0 });
  const showTip = (at: "terms" | "joint" | "privacy" | "all") => setTip((t) => ({ at, seq: t.seq + 1 }));
  const tipSeqFor = (at: "terms" | "joint" | "privacy" | "all") => (tip.at === at ? tip.seq : 0);
  // 끝까지 내리면 그 항목을 자동 체크(처음 한 번만 — 이후 사용자가 풀 수 있음)
  const markTermsRead = useCallback(() => {
    setTermsRead(true);
    setTermsConsent(true);
  }, []);
  const markJointUseRead = useCallback(() => {
    setJointUseRead(true);
    setJointUseConsent(true);
  }, []);
  const markPrivacyNoticeRead = useCallback(() => {
    setPrivacyNoticeRead(true);
    setPrivacyNoticeConsent(true);
  }, []);
  // 이용 규정 본문 — 공개 문서 terms.html 한 벌을 그대로 보여 준다. null = 불러오는 중
  const [termsHtml, setTermsHtml] = useState<string | null>(null);
  const [termsLoadFailed, setTermsLoadFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    loadLegalDocBody("/terms.html")
      .then((html) => alive && setTermsHtml(html))
      .catch(() => alive && setTermsLoadFailed(true));
    return () => {
      alive = false;
    };
  }, []);

  const strength = passwordStrength(password);
  const confirmState = !passwordConfirm ? "" : passwordConfirm === password ? "match" : "mismatch";

  // 가입 신청 검사 오류 — 위에서부터 첫 번째 칸 하나만. 그 칸 값을 고치면(오류 때 값과 달라지면) 풀린다
  const [fieldErr, setFieldErr] = useState<{ field: FieldKey; msg: string; value: string; seq: number } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const fieldValue = (f: FieldKey): string =>
    ({
      tenant,
      name,
      id: loginId,
      password,
      passwordConfirm,
      email: `${emailLocal}@${emailDomain}`,
      phone,
      birth: birthDate,
    })[f];
  const activeErr = fieldErr && fieldValue(fieldErr.field) === fieldErr.value ? fieldErr : null;
  const boxProps = (f: FieldKey) => ({
    field: f,
    error: activeErr?.field === f ? activeErr.msg : null,
    tipSeq: fieldErr?.seq ?? 0,
  });
  /** 걸린 칸 표시 — 빨간 테두리·말풍선(상태) + 떨림·커서 이동(바로) */
  function flagField(field: FieldKey, msg: string) {
    setFieldErr((e) => ({ field, msg, value: fieldValue(field), seq: (e?.seq ?? 0) + 1 }));
    const box = formRef.current?.querySelector<HTMLElement>(`[data-field="${field}"]`) ?? null;
    shake(box);
    const inputs = [...(box?.querySelectorAll("input") ?? [])].filter((i) => !i.readOnly);
    // 입력칸이 없는 칸(근무 법인 선택)은 선택 버튼으로 커서
    (inputs.find((i) => !i.value) ?? inputs[0] ?? box?.querySelector<HTMLElement>(".select-trigger"))?.focus();
  }

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [done, setDone] = useState(false);

  /** 화면 순서(위 → 아래)대로 검사해 첫 번째 문제 칸 하나만 돌려준다 */
  function validate(): { field: FieldKey; msg: string } | null {
    if (!tenant) return { field: "tenant", msg: "근무 법인을 고르세요" };
    if (!name.trim()) return { field: "name", msg: "이름을 입력하세요" };
    if (!loginId.trim()) return { field: "id", msg: "아이디를 입력하세요" };
    if (!password) return { field: "password", msg: "비밀번호를 입력하세요" };
    if (password.length < PASSWORD_MIN_LENGTH)
      return { field: "password", msg: `비밀번호는 ${PASSWORD_MIN_LENGTH}자 이상이어야 합니다` };
    if (!passwordConfirm) return { field: "passwordConfirm", msg: "비밀번호를 한 번 더 입력하세요" };
    if (password !== passwordConfirm) return { field: "passwordConfirm", msg: "비밀번호가 일치하지 않습니다" };
    if (!emailLocal.trim()) return { field: "email", msg: "이메일을 입력하세요" };
    if (!emailDomain.trim()) return { field: "email", msg: "이메일 도메인을 입력하거나 고르세요" };
    if (!EMAIL_RE.test(personalEmail) || personalEmail.length > 100) return { field: "email", msg: "올바른 이메일 주소가 아닙니다" };
    const phoneDigits = phone.replace(/\D/g, "").length;
    if (phoneDigits === 0) return { field: "phone", msg: "연락처를 입력하세요" };
    if (phoneDigits < 10) return { field: "phone", msg: "휴대폰 번호를 끝까지 입력하세요" };
    if (!birthDate) return { field: "birth", msg: "생년월일을 입력하세요" };
    return null;
  }

  function setAllConsents(checked: boolean) {
    setTermsConsent(checked);
    setJointUseConsent(checked);
    setPrivacyNoticeConsent(checked);
  }

  function goToAccountStep() {
    if (!allAgreed) {
      setErrorMsg("필수 항목에 모두 동의해야 가입할 수 있습니다.");
      return;
    }
    setErrorMsg("");
    setStep(2);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = validate();
    if (v) {
      setErrorMsg("");
      flagField(v.field, v.msg);
      return;
    }
    setErrorMsg("");
    setSubmitting(true);
    try {
      await signup({
        tenant: tenant as TenantKey,
        loginId: loginId.trim(),
        password,
        name: name.trim(),
        phone: phone.trim(),
        personalEmail: personalEmail.trim(),
        birthDate,
        termsConsent,
        jointUseConsent,
        privacyNoticeConsent,
      });
      setDone(true);
    } catch (err) {
      const code = err instanceof AuthError ? err.code : "UNKNOWN";
      setSubmitting(false);
      if (code === "ID_TAKEN") {
        // 아이디 칸에서 바로 고치도록 — 칸 아래 문구 + 말풍선·떨림·커서 이동, 아래쪽 공용 문구는 비움
        setTakenId(loginId.trim());
        setErrorMsg("");
        flagField("id", "이미 사용 중인 아이디입니다");
        return;
      }
      setErrorMsg(ERROR_MESSAGE[code] ?? `가입 실패: ${(err as Error).message}`);
    }
  }

  if (done) {
    return (
      <div className="login-body">
        <main className="login-card">
          <div className="login-brand">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>{" "}
            가입 신청 완료
          </div>
          <p style={{ textAlign: "center", color: "var(--c-text-muted)", fontSize: "var(--fs-base)", lineHeight: 1.6 }}>
            가입 신청이 접수되었습니다.
            <br />
            관리자 승인 후 로그인할 수 있습니다.
          </p>
          <div className="login-alt">
            <Link to="/login">로그인 화면으로</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="login-body">
      <main className="login-card signup-card">
        {step === 2 && (
          <button
            type="button"
            className="signup-back"
            aria-label="약관 동의로 돌아가기"
            disabled={submitting}
            onClick={() => {
              setErrorMsg("");
              setStep(1);
            }}
          >
            <Icon name="back" size={20} />
          </button>
        )}
        <div className="login-brand">
          회원가입
        </div>

        <ol className="signup-steps" aria-label="가입 단계">
          <li className={step === 1 ? "is-current" : "is-done"} aria-current={step === 1 ? "step" : undefined}>
            <span className="signup-step-no">1</span>약관 동의
          </li>
          <li className={step === 2 ? "is-current" : undefined} aria-current={step === 2 ? "step" : undefined}>
            <span className="signup-step-no">2</span>계정 정보
          </li>
        </ol>

        {step === 1 ? (
          <div className="login-form">
            <div className="signup-consent">
              <div className="signup-consent-all">
                <label className={`signup-consent-check${allRead ? "" : " is-locked"}`}>
                  <input
                    type="checkbox"
                    checked={allAgreed}
                    aria-disabled={!allRead || undefined}
                    onChange={(e) => {
                      if (allRead) {
                        setAllConsents(e.target.checked);
                        return;
                      }
                      // 안 읽은 박스만 빨간 테두리·떨림, 말풍선은 이 줄에만
                      setNudged((n) => ({
                        terms: termsRead ? n.terms : n.terms + 1,
                        joint: jointUseRead ? n.joint : n.joint + 1,
                        privacy: privacyNoticeRead ? n.privacy : n.privacy + 1,
                      }));
                      showTip("all");
                    }}
                  />
                  <span>필수 항목 모두 동의</span>
                </label>
                {tipSeqFor("all") > 0 && !allRead && (
                  <ClickTip key={tip.seq} text="아래 항목을 모두 끝까지 내리면 선택할 수 있습니다" />
                )}
              </div>

              <ConsentItem
                title="ERP 이용 규정 동의"
                checked={termsConsent}
                onChange={setTermsConsent}
                read={termsRead}
                onRead={markTermsRead}
                nudgeCount={nudged.terms}
                tipSeq={tipSeqFor("terms")}
                onNudge={() => {
                  setNudged((n) => ({ ...n, terms: n.terms + 1 }));
                  showTip("terms");
                }}
                ready={termsHtml !== null}
                fullHref="/terms.html"
              >
                {termsHtml !== null ? (
                  <div className="signup-consent-doc" dangerouslySetInnerHTML={{ __html: termsHtml }} />
                ) : termsLoadFailed ? (
                  <p className="signup-consent-more">
                    이용 규정을 불러오지 못했습니다.{" "}
                    <a href="/terms.html" target="_blank" rel="noopener noreferrer" onClick={markTermsRead}>
                      새 창에서 읽기
                    </a>
                    를 누르면 체크할 수 있습니다.
                  </p>
                ) : (
                  <p className="signup-consent-more">불러오는 중…</p>
                )}
              </ConsentItem>

              <ConsentItem
                title="중앙산업·경원산업 계정 정보 공동 이용 동의"
                checked={jointUseConsent}
                onChange={setJointUseConsent}
                read={jointUseRead}
                onRead={markJointUseRead}
                nudgeCount={nudged.joint}
                tipSeq={tipSeqFor("joint")}
                onNudge={() => {
                  setNudged((n) => ({ ...n, joint: n.joint + 1 }));
                  showTip("joint");
                }}
              >
                <NoticeList rows={JOINT_USE_CONSENT_NOTICE} />
              </ConsentItem>

              <ConsentItem
                title="개인정보 수집·이용 안내 확인"
                checked={privacyNoticeConsent}
                onChange={setPrivacyNoticeConsent}
                read={privacyNoticeRead}
                onRead={markPrivacyNoticeRead}
                nudgeCount={nudged.privacy}
                tipSeq={tipSeqFor("privacy")}
                onNudge={() => {
                  setNudged((n) => ({ ...n, privacy: n.privacy + 1 }));
                  showTip("privacy");
                }}
                fullHref="/privacy.html"
              >
                <NoticeList rows={ACCOUNT_PRIVACY_NOTICE} />
                <p className="signup-consent-more">자세한 내용은 개인정보처리방침에 있습니다.</p>
              </ConsentItem>
            </div>

            <button type="button" className="btn-primary" disabled={!allAgreed} onClick={goToAccountStep}>
              다음
            </button>
            <div className="login-msg" role="alert">
              {errorMsg}
            </div>
          </div>
        ) : (
          <form ref={formRef} className="login-form" autoComplete="off" noValidate onSubmit={onSubmit}>
            {/* noValidate — 브라우저 기본 검사 말풍선 대신 validate() 순서(위 → 아래)로 첫 문제 칸에 우리 말풍선 */}
            {/* 필드 형식은 KRDS 기반 hanui 가입 양식을 따름 — 위 이름표 · 48px 입력칸 · 칸 안 "…을 입력하세요" */}
            <div className="signup-fields">
              <div className="field">
                <span id="signup-tenant-lbl">근무 법인</span>
                <FieldBox {...boxProps("tenant")}>
                  <div className="signup-tenant-select">
                    <Select
                      value={tenant}
                      options={TENANT_OPTIONS}
                      placeholder="근무 법인을 고르세요"
                      ariaLabel="근무 법인"
                      onChange={(v) => setTenant(isTenantKey(v) ? v : "")}
                    />
                  </div>
                </FieldBox>
                <p className="signup-field-msg is-wrap">지금 실제로 일하고 있는 법인을 고르세요. 해당 법인 관리자가 가입을 승인합니다.</p>
              </div>
              <div className="field">
                <label htmlFor="signup-name">이름</label>
                <FieldBox {...boxProps("name")}>
                  <input
                    id="signup-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="이름을 입력하세요"
                    autoComplete="name"
                    maxLength={40}
                    required
                    autoFocus
                  />
                </FieldBox>
              </div>
              <div className="field">
                <label htmlFor="signup-id">아이디</label>
                <FieldBox {...boxProps("id")}>
                  <input
                    id="signup-id"
                    type="text"
                    value={loginId}
                    // 아이디는 소문자만 — 대문자를 쳐도 바로 소문자로 바꾼다
                    onChange={(e) => setLoginId(hangulToQwerty(e.target.value).toLowerCase().replace(ID_ALLOW, ""))}
                    placeholder="아이디를 입력하세요"
                    autoComplete="username"
                    autoCapitalize="none"
                    lang="en"
                    inputMode="email"
                    required
                    aria-invalid={idTaken || undefined}
                    aria-describedby="signup-id-msg"
                  />
                </FieldBox>
                <p id="signup-id-msg" className={`signup-field-msg${idTaken ? " is-error" : ""}`} aria-live="polite">
                  {idTaken ? "이미 사용 중인 아이디입니다. 다른 아이디를 입력하세요" : "영문 소문자·숫자로 입력하세요"}
                </p>
              </div>
              <div className="field">
                <label htmlFor="signup-password">비밀번호</label>
                <FieldBox {...boxProps("password")}>
                  <PasswordInput
                    id="signup-password"
                    value={password}
                    onChange={setPassword}
                    placeholder="비밀번호를 입력하세요"
                    describedBy="signup-password-strength"
                  />
                </FieldBox>
                <div id="signup-password-strength" className={`signup-pw-strength is-lv${strength.level}`} aria-live="polite">
                  <span className="signup-pw-bars" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="signup-pw-text">
                    {strength.level === 0
                      ? `${PASSWORD_MIN_LENGTH}자 이상, 영문·숫자·특수문자를 섞으면 안전합니다`
                      : `보안 수준: ${strength.label}`}
                  </span>
                </div>
              </div>
              <div className="field">
                <label htmlFor="signup-password-confirm">비밀번호 확인</label>
                <FieldBox {...boxProps("passwordConfirm")}>
                  <PasswordInput
                    id="signup-password-confirm"
                    value={passwordConfirm}
                    onChange={setPasswordConfirm}
                    placeholder="비밀번호를 다시 입력하세요"
                    invalid={confirmState === "mismatch"}
                    describedBy="signup-password-confirm-msg"
                  />
                </FieldBox>
                {/* 입력 전에도 안내 한 줄을 두어 일치 문구가 나올 때 높이가 바뀌지 않게 */}
                <p
                  id="signup-password-confirm-msg"
                  className={`signup-field-msg${confirmState ? ` is-${confirmState}` : ""}`}
                  aria-live="polite"
                >
                  {confirmState === "match"
                    ? "비밀번호가 일치합니다"
                    : confirmState === "mismatch"
                      ? "비밀번호가 일치하지 않습니다"
                      : "같은 비밀번호를 한 번 더 입력하세요"}
                </p>
              </div>
              <div className="field">
                <label htmlFor="signup-email">이메일</label>
                {/* 한국형 분리 입력 — [아이디] @ [도메인] [선택]. 도메인을 고르면 칸이 채워지고 잠김, "직접 입력"이면 비우고 풀림 */}
                <FieldBox {...boxProps("email")}>
                  <div className="signup-email">
                    <input
                      id="signup-email"
                      type="text"
                      value={emailLocal}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\s/g, "");
                        const at = v.indexOf("@");
                        if (at < 0) {
                          setEmailLocal(v);
                          return;
                        }
                        // 전체 주소를 붙여 넣거나 @ 를 친 경우 — 도메인 칸으로 나눠 넣고 커서 이동
                        const domain = v.slice(at + 1).replace(/@/g, "").toLowerCase();
                        setEmailLocal(v.slice(0, at));
                        if (domain) {
                          setEmailDomain(domain);
                          setEmailDomainChoice(EMAIL_DOMAINS.includes(domain) ? domain : EMAIL_DOMAIN_DIRECT);
                        }
                        if (!domain && emailDomainChoice === EMAIL_DOMAIN_DIRECT) {
                          emailDomainRef.current?.focus();
                        }
                      }}
                      placeholder="이메일"
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="none"
                      required
                    />
                    <span className="signup-email-at" aria-hidden="true">
                      @
                    </span>
                    <input
                      ref={emailDomainRef}
                      id="signup-email-domain"
                      type="text"
                      value={emailDomain}
                      onChange={(e) => setEmailDomain(e.target.value.replace(/[\s@]/g, "").toLowerCase())}
                      placeholder="직접 입력"
                      aria-label="이메일 도메인"
                      inputMode="email"
                      autoCapitalize="none"
                      readOnly={emailDomainChoice !== EMAIL_DOMAIN_DIRECT}
                      required
                    />
                    <div className="signup-email-select">
                      <Select
                        value={emailDomainChoice}
                        options={EMAIL_DOMAIN_OPTIONS}
                        ariaLabel="이메일 도메인 선택"
                        onChange={(v) => {
                          setEmailDomainChoice(v);
                          if (v === EMAIL_DOMAIN_DIRECT) {
                            setEmailDomain("");
                            requestAnimationFrame(() => emailDomainRef.current?.focus());
                          } else {
                            setEmailDomain(v);
                          }
                        }}
                      />
                    </div>
                  </div>
                </FieldBox>
              </div>
              <div className="field">
                <label htmlFor="signup-phone">연락처</label>
                <FieldBox {...boxProps("phone")}>
                  <input
                    id="signup-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhoneKR(e.target.value))}
                    placeholder="휴대폰 번호를 입력하세요"
                    autoComplete="tel"
                    inputMode="tel"
                    maxLength={13}
                    required
                  />
                </FieldBox>
              </div>
              <label className="field">
                <span>생년월일</span>
                {/* 사용자 관리(UserFormModal)와 같은 입력기 — 연·월·일 칸 + 달력 */}
                <FieldBox {...boxProps("birth")}>
                  <DatePicker value={birthDate} onChange={setBirthDate} minYear={1900} fullWidth ariaLabel="생년월일" />
                </FieldBox>
              </label>
            </div>

            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "가입 신청 중..." : "가입 신청"}
            </button>
            <div className="login-msg" role="alert">
              {errorMsg}
            </div>
          </form>
        )}

        <div className="login-alt">
          이미 계정이 있으신가요? <Link to="/login">로그인</Link>
        </div>

        <div className="login-foot">
          가입 신청 후 관리자 승인이 필요합니다
          <LegalLinks />
        </div>
      </main>
    </div>
  );
}
